/**
 * Маркировка версии интерфейса и сборка диагностического блока для баг-репорта.
 *
 * Версия бандла приходит из `__BUILD_INFO__` — его подставляет `viteConf.define`
 * в `quasar.config.ts` (в docker-сборке значения идут из `BUILD_VERSION`/`BUILD_SHA`,
 * локально — из git). Формат строки версии СОВПАДАЕТ с `resolveServiceVersion()`
 * узлов (`@omniflow/heartbeat`): `0.66.0-abc1234`. Совпадение намеренное — админ
 * сравнивает версию на плашке с версиями узлов в `/health` глазами, без перевода.
 *
 * Функции здесь чистые и все входы получают параметрами: `window`/`navigator` не
 * трогаем, иначе модуль не собрать в node-окружении vitest.
 */

export interface BuildInfo {
  version: string | null
  commit: string | null
  /** Рабочее дерево на момент сборки было грязным (суффикс `+` у git sha). */
  dirty: boolean
  branch: string | null
  /** ISO-8601 времени сборки. */
  date: string | null
}

/** Компонент платформы в отчёте (проекция `ComponentInfo` из `q.system.info`). */
export interface DiagnosticsComponent {
  title: string
  version: string | null
  reachable: boolean
}

/** Узел флота в отчёте (проекция `NodeInfo` из `q.system.info`). */
export interface DiagnosticsNode {
  serviceName: string
  aliveCount: number
  staleCount: number
  versions: string[]
  roles: string[]
}

/**
 * Серверная часть отчёта. Её отдаёт `q.system.info`, доступный только
 * администратору установки, поэтому в отчёте она опциональна: у админа тенанта
 * и супервайзера блок просто не печатается.
 */
export interface DiagnosticsServerFacts {
  version: string
  components: DiagnosticsComponent[]
  nodes: DiagnosticsNode[]
  database: { applied: number | null; pending: string[] | null }
}

export interface DiagnosticsContext {
  build: BuildInfo
  tenantId: string | null
  roles: string[]
  username: string | null
  /** Полный адрес страницы — по хосту видно, с какого стенда пришёл баг. */
  url: string
  userAgent: string
  now: Date
  server?: DiagnosticsServerFacts | null
}

const EMPTY_BUILD_INFO: BuildInfo = {
  version: null,
  commit: null,
  dirty: false,
  branch: null,
  date: null,
}

/**
 * Читает информацию о сборке из бандла.
 *
 * `typeof`-guard обязателен: при запуске без vite-define (юнит-тест, сторонний
 * сборщик) обращение к необъявленной глобали — ReferenceError, и плашка утащила
 * бы за собой всё приложение.
 */
export const readBuildInfo = (): BuildInfo => {
  if (typeof __BUILD_INFO__ === 'undefined') return EMPTY_BUILD_INFO
  return __BUILD_INFO__
}

/**
 * `0.66.0-abc1234+`. Грязное дерево — плюсом, как у образов (`build-images.sh`).
 * Версия неизвестна — `dev`: строка всегда непустая, потому что пустая плашка
 * читается как «версии нет», а это ложь — версия просто не доехала.
 */
export const formatBuildVersion = (info: BuildInfo): string => {
  const version = info.version ?? 'dev'
  const commit = info.commit ? `-${info.commit}${info.dirty ? '+' : ''}` : ''
  return `${version}${commit}`
}

/** Подсказка при наведении на плашку: ветка, время сборки, грязь. */
export const formatBuildTitle = (info: BuildInfo): string => {
  const parts: string[] = []
  if (info.branch) parts.push(`ветка: ${info.branch}`)
  if (info.date) parts.push(`собрано: ${info.date}`)
  if (info.dirty) parts.push('рабочее дерево было грязным')
  parts.push('клик — скопировать данные для отчёта об ошибке')
  return parts.join('\n')
}

/**
 * Короткое человекочитаемое имя браузера. Порядок проверок важен: Яндекс.Браузер
 * и Edge представляются и как `Chrome/NNN`, поэтому их узнаём раньше Chrome,
 * а Chrome — раньше Safari (он тоже шлёт `Safari/`).
 */
export const describeBrowser = (userAgent: string): string => {
  const os = ((): string => {
    if (/Windows NT/.test(userAgent)) return 'Windows'
    if (/Android/.test(userAgent)) return 'Android'
    if (/iPhone|iPad|iPod/.test(userAgent)) return 'iOS'
    if (/Mac OS X/.test(userAgent)) return 'macOS'
    if (/Linux/.test(userAgent)) return 'Linux'
    return ''
  })()

  const browser = ((): string => {
    const match = (re: RegExp): string | null => re.exec(userAgent)?.[1] ?? null
    const yandex = match(/YaBrowser\/(\d+)/)
    if (yandex) return `Яндекс.Браузер ${yandex}`
    const edge = match(/Edg(?:e|A|iOS)?\/(\d+)/)
    if (edge) return `Edge ${edge}`
    const opera = match(/OPR\/(\d+)/)
    if (opera) return `Opera ${opera}`
    const firefox = match(/Firefox\/(\d+)/)
    if (firefox) return `Firefox ${firefox}`
    const chrome = match(/Chrome\/(\d+)/)
    if (chrome) return `Chrome ${chrome}`
    const safari = /Safari\//.test(userAgent) ? match(/Version\/(\d+)/) : null
    if (safari) return `Safari ${safari}`
    return ''
  })()

  if (!browser && !os) return 'неизвестен'
  if (!browser) return `неизвестен / ${os}`
  if (!os) return browser
  return `${browser} / ${os}`
}

/** `UTC+3`, `UTC-5:30` — смещение зоны браузера в момент снимка. */
const formatTimezoneOffset = (now: Date): string => {
  const totalMinutes = -now.getTimezoneOffset()
  const sign = totalMinutes < 0 ? '-' : '+'
  const hours = Math.floor(Math.abs(totalMinutes) / 60)
  const minutes = Math.abs(totalMinutes) % 60
  return `UTC${sign}${hours}${minutes ? `:${String(minutes).padStart(2, '0')}` : ''}`
}

/** Выровненная строка отчёта. Ширина метки — по самой длинной («Компоненты:»). */
const line = (label: string, value: string): string => `${`${label}:`.padEnd(12)}${value}`

/**
 * Готовый текст для вставки в задачу или чат.
 *
 * Правило одно: чего не знаем — того не печатаем. Строка «Тенант: null» выглядит
 * как факт и уводит разбор, поэтому неизвестные поля выпадают из отчёта целиком.
 */
export const buildDiagnosticsReport = (ctx: DiagnosticsContext): string => {
  const lines: string[] = ['OmniFlow — данные для отчёта об ошибке', '']

  lines.push(line('Интерфейс', formatBuildVersion(ctx.build)))

  const buildParts: string[] = []
  if (ctx.build.branch) buildParts.push(`ветка ${ctx.build.branch}`)
  if (ctx.build.date) buildParts.push(ctx.build.date)
  if (ctx.build.dirty) buildParts.push('рабочее дерево грязное')
  if (buildParts.length) lines.push(line('Сборка', buildParts.join(' · ')))

  if (ctx.tenantId) {
    const who = [
      ctx.roles.length ? `роль ${ctx.roles.join(', ')}` : null,
      ctx.username ? `пользователь ${ctx.username}` : null,
    ].filter((part): part is string => part !== null)
    lines.push(line('Тенант', [ctx.tenantId, ...who].join(' · ')))
  }

  lines.push(line('Адрес', ctx.url))
  lines.push(line('Браузер', describeBrowser(ctx.userAgent)))
  lines.push(
    line(
      'Время',
      `${ctx.now.toLocaleString('ru-RU', { hour12: false })} (${formatTimezoneOffset(ctx.now)})`,
    ),
  )
  lines.push(line('UA', ctx.userAgent))

  const server = ctx.server
  if (server) {
    lines.push('')
    lines.push(line('Платформа', server.version))

    if (server.components.length) {
      const components = server.components
        .map((comp) => {
          const version = comp.version ?? 'версия неизвестна'
          return comp.reachable ? `${comp.title} ${version}` : `${comp.title} (недоступен)`
        })
        .join(' · ')
      lines.push(line('Компоненты', components))
    }

    for (const node of server.nodes) {
      const stale = node.staleCount > 0 ? `, молчит ${node.staleCount}` : ''
      const parts = [
        `${node.serviceName} — живых ${node.aliveCount}${stale}`,
        node.versions.length ? node.versions.join(', ') : null,
        node.roles.length ? `роли ${node.roles.join(',')}` : null,
      ].filter((part): part is string => part !== null)
      lines.push(line('Узел', parts.join(' · ')))
    }

    const applied = server.database.applied
    const pending = server.database.pending
    if (applied !== null || pending !== null) {
      const db = [
        applied !== null ? `применено ${applied}` : null,
        pending !== null ? `не накачено ${pending.length}` : null,
      ].filter((part): part is string => part !== null)
      lines.push(line('Схема БД', db.join(', ')))
    }
  }

  return lines.join('\n')
}
